import React, { useCallback, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import RoundedModal from '../../../../../../../molecules/RoundedModal'
import SydButton from '../../../../../../../commonDesign/Button'
import SydModalActions from '../../../../../../../commonDesign/SydModal/SydModalActions'

const INITIAL_STATE = { open: false, processing: false, comment: null, error: null }

const DeleteCommentConfirmModal = React.forwardRef(function DeleteCommentConfirmModal ({ onConfirm }, ref) {
  const [dialogState, setDialogState] = useState(INITIAL_STATE)

  const close = useCallback(() => {
    setDialogState(INITIAL_STATE)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!dialogState.comment) return
    setDialogState((prev) => ({ ...prev, processing: true, error: null }))
    try {
      await onConfirm(dialogState.comment.commentId)
      setDialogState(INITIAL_STATE)
    } catch (error) {
      setDialogState((prev) => ({
        ...prev,
        processing: false,
        error: 'Failed to delete comment. Please try again.'
      }))
    }
  }, [dialogState.comment, onConfirm])

  useImperativeHandle(ref, () => ({
    open: (comment) => {
      setDialogState({ open: true, processing: false, comment, error: null })
    },
    close
  }), [close])

  return (
    <RoundedModal
      title='Delete Comment'
      open={dialogState.open}
      onClose={dialogState.processing ? undefined : close}
      size='small'
      position='top'
      disablePortal={false}
    >
      <div>
        {dialogState.open ? (
          <>
            <p className='font-size-sm m-0'>
              Are you sure you want to delete this comment? This action cannot be undone.
            </p>
            {dialogState.error && (
              <p className='font-size-xs text-danger-500 m-0' style={{ marginTop: 'var(--dim-2)' }} role='alert'>
                {dialogState.error}
              </p>
            )}
            <SydModalActions style={{ marginTop: 'var(--dim-4)' }}>
              <SydButton
                disabled={dialogState.processing}
                variant='ghost'
                size='md'
                onClick={close}
              >
                Cancel
              </SydButton>
              <SydButton
                processing={dialogState.processing}
                variant='primary'
                priority='warning'
                icon='delete'
                size='md'
                onClick={handleConfirm}
              >
                Delete
              </SydButton>
            </SydModalActions>
          </>
        ) : null}
      </div>
    </RoundedModal>
  )
})

DeleteCommentConfirmModal.propTypes = {
  onConfirm: PropTypes.func.isRequired
}

export default DeleteCommentConfirmModal
